import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import SiteFooter from "@/components/SiteFooter";

const UI = {
    nl: { back: "Terug naar home", updated: "Laatst bijgewerkt", other: "Andere documenten", docs: { privacy: "Privacyverklaring", terms: "Algemene voorwaarden", cookies: "Cookiebeleid" } },
    fr: { back: "Retour à l'accueil", updated: "Dernière mise à jour", other: "Autres documents", docs: { privacy: "Déclaration de confidentialité", terms: "Conditions générales", cookies: "Politique de cookies" } },
    en: { back: "Back to home", updated: "Last updated", other: "Other documents", docs: { privacy: "Privacy policy", terms: "Terms & conditions", cookies: "Cookie policy" } },
    es: { back: "Volver al inicio", updated: "Última actualización", other: "Otros documentos", docs: { privacy: "Política de privacidad", terms: "Términos y condiciones", cookies: "Política de cookies" } },
};

const PATHS = { privacy: "/privacy", terms: "/voorwaarden", cookies: "/cookies" };

export default function LegalLayout({ docKey, content, footerExtra }) {
    const { i18n } = useTranslation();
    const lang = ["nl", "fr", "en", "es"].includes((i18n.language || "nl").slice(0, 2)) ? (i18n.language || "nl").slice(0, 2) : "nl";
    const ui = UI[lang];
    const doc = content[lang] || content.nl;
    const [info, setInfo] = useState({ company_name: "Social Factory", btw_number: "", email: "info@social-factory.be" });

    useEffect(() => {
        api.get("/public/company-info").then((r) => setInfo(r.data)).catch(() => {});
        window.scrollTo(0, 0);
    }, []);

    const fill = (s) =>
        String(s)
            .replaceAll("{{company}}", info.company_name || "Social Factory")
            .replaceAll("{{btw}}", info.btw_number || "—")
            .replaceAll("{{email}}", info.email || "info@social-factory.be");

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 font-body" data-testid={`legal-page-${docKey}`}>
            <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-10 pb-20">
                <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 mb-8" data-testid="legal-back-link">
                    <ArrowLeft className="h-4 w-4" /> {ui.back}
                </Link>
                <div className="text-xs uppercase tracking-[0.3em] text-[#E5A93D] font-semibold mb-3">{ui.docs[docKey]}</div>
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-2" data-testid="legal-title">{doc.title}</h1>
                <p className="text-xs text-zinc-500 mb-10">{ui.updated}: {doc.updated}</p>

                <div className="space-y-8">
                    {doc.sections.map((s, i) => (
                        <section key={i} data-testid={`legal-section-${i}`}>
                            <h2 className="font-display font-bold text-lg tracking-tight mb-2">{i + 1}. {fill(s.h)}</h2>
                            {s.p.map((para, j) => (
                                <p key={j} className="text-sm text-zinc-600 leading-relaxed mb-2">{fill(para)}</p>
                            ))}
                        </section>
                    ))}
                </div>

                {footerExtra}

                <div className="mt-12 pt-6 border-t border-zinc-200">
                    <p className="text-xs uppercase tracking-wider text-zinc-500 mb-3">{ui.other}</p>
                    <div className="flex flex-wrap gap-4 text-sm">
                        {Object.keys(PATHS).filter((k) => k !== docKey).map((k) => (
                            <Link key={k} to={PATHS[k]} className="text-[#E5A93D] hover:underline">{ui.docs[k]}</Link>
                        ))}
                    </div>
                </div>
            </div>
            <SiteFooter />
        </div>
    );
}
